"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { Trophy, X, Clock, Minus, ChevronDown, Loader2 } from "lucide-react";
interface PredictionHistoryProps {
  labels: Record<string, string>;
}
interface TeamModel {
  name: string;
  shortName?: string;
  assets?: {
    logo?: string;
  };
}
interface ScoresType {
  homeGoals: number | null;
  awayGoals: number | null;
}
interface MatchModel {
  id: string;
  homeTeam: TeamModel;
  awayTeam: TeamModel;
  scores?: {
    ftScore?: ScoresType;
    htScore?: ScoresType;
    aetScore?: ScoresType;
    penScore?: ScoresType;
  };
  kickoffAt?: string;
}
interface PredictionFixture {
  matchId: string;
  market: string;
  matchModel?: MatchModel;
  prediction?: {
    value: string | number;
  };
  result?: {
    outcome: string;
    points: number;
    status: string;
  };
}
interface PredictionItem {
  id: string;
  gameType: string;
  status: string;
  points: number;
  fixtures: PredictionFixture[];
  createdAt: string;
  totalFixtures: number;
  settledFixtures: number;
}
interface PredictionsResponse {
  meta: {
    pagination: {
      nextPageStartsAfter: string;
    };
  };
  data: PredictionItem[];
}
const statusConfig: Record<string, {
  bg: string;
  text: string;
  icon: React.ElementType;
}> = {
  WON: {
    bg: "bg-green-500/10",
    text: "text-green-600",
    icon: Trophy
  },
  LOST: {
    bg: "bg-red-500/10",
    text: "text-red-600",
    icon: X
  },
  ACTIVE: {
    bg: "bg-yellow-500/10",
    text: "text-yellow-600",
    icon: Clock
  },
  PARTIALLY_WON: {
    bg: "bg-orange-500/10",
    text: "text-orange-600",
    icon: Trophy
  },
  CANCELED: {
    bg: "bg-gray-500/10",
    text: "text-gray-600",
    icon: Minus
  }
};
const MARKET_LABEL_MAP: Record<string, {
  key: string;
  fallback: string;
}> = {
  FT_1X2: {
    key: "marketFT1X2",
    fallback: "Full Time Result"
  },
  HT_1X2: {
    key: "marketHT1X2",
    fallback: "Half Time Result"
  },
  HT_FT: {
    key: "marketHTFT",
    fallback: "HT / FT"
  },
  CORRECT_SCORE: {
    key: "marketCorrectScore",
    fallback: "Correct Score"
  },
  CORRECT_SCORE_HT: {
    key: "marketCorrectScoreHT",
    fallback: "Correct Score (HT)"
  },
  CORRECT_SCORE_ADVANCED: {
    key: "marketCorrectScoreAdvanced",
    fallback: "Correct Score Adv."
  },
  BTTS: {
    key: "marketBTTS",
    fallback: "Both Teams Score"
  },
  OVER_UNDER: {
    key: "marketOverUnder",
    fallback: "Over/Under"
  },
  DOUBLE_CHANCE: {
    key: "marketDoubleChance",
    fallback: "Double Chance"
  },
  PLAYER_GOALSCORER: {
    key: "marketPlayerGoalscorer",
    fallback: "Goalscorer"
  }
};
function formatMarketName(market: string, labels: Record<string, string>): string {
  const mapping = MARKET_LABEL_MAP[market];
  if (mapping) return labels[mapping.key] || mapping.fallback;
  return market.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/** Format the user's prediction value for display */
function formatPredictionValue(market: string, value: string | number | undefined): string {
  if (value === undefined || value === null) return "—";
  const v = String(value);
  if (market === "FT_1X2" || market === "HT_1X2") {
    if (v === "1") return "Home";
    if (v.toLowerCase() === "x") return "Draw";
    if (v === "2") return "Away";
  }
  if (market === "DOUBLE_CHANCE") {
    if (v === "1x") return "Home/Draw";
    if (v === "12") return "Home/Away";
    if (v === "x2") return "Draw/Away";
  }
  if (market === "BTTS") {
    if (v.toLowerCase() === "yes") return "Yes";
    if (v.toLowerCase() === "no") return "No";
  }
  return v;
}
function formatScore(scores: MatchModel["scores"]): string | null {
  if (!scores) return null;
  // Try FT first, then HT, then AET
  const ft = scores.ftScore;
  if (ft && ft.homeGoals != null && ft.awayGoals != null) {
    return `${ft.homeGoals} : ${ft.awayGoals}`;
  }
  const ht = scores.htScore;
  if (ht && ht.homeGoals != null && ht.awayGoals != null) {
    return `${ht.homeGoals} : ${ht.awayGoals}`;
  }
  const aet = scores.aetScore;
  if (aet && aet.homeGoals != null && aet.awayGoals != null) {
    return `${aet.homeGoals} : ${aet.awayGoals}`;
  }
  return null;
}
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric"
});

/** Determine if all fixtures in a prediction share the same match (e.g. MATCH_QUIZ) */
function isSingleMatchPrediction(prediction: PredictionItem): boolean {
  if (prediction.fixtures.length <= 1) return true;
  const firstMatchId = prediction.fixtures[0]?.matchId;
  return prediction.fixtures.every(f => f.matchId === firstMatchId);
}

/** Render the match row: Home [score] Away */
function MatchRow(t0) {
  const $ = _c(12);
  const {
    match
  } = t0;
  let t1;
  if ($[0] !== match.scores) {
    t1 = formatScore(match.scores);
    $[0] = match.scores;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const score = t1;
  const homeName = match.homeTeam?.shortName || match.homeTeam?.name || "\u2014";
  const awayName = match.awayTeam?.shortName || match.awayTeam?.name || "\u2014";
  let t2;
  if ($[2] !== homeName) {
    t2 = <span className="font-medium text-foreground truncate flex-1 text-right">{homeName}</span>;
    $[2] = homeName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = score || "vs";
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="shrink-0 rounded bg-muted px-2 py-0.5 text-xs font-bold text-foreground tabular-nums">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== awayName) {
    t5 = <span className="font-medium text-foreground truncate flex-1">{awayName}</span>;
    $[6] = awayName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex items-center justify-between gap-2 text-sm">{t2}{t4}{t5}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/** Render prediction + market info for a single fixture */
function FixturePredictionRow(t0) {
  const $ = _c(14);
  const {
    fixture,
    labels,
    compact
  } = t0;
  const t1 = fixture.prediction?.value;
  let t2;
  if ($[0] !== fixture.market || $[1] !== t1) {
    t2 = formatPredictionValue(fixture.market, t1);
    $[0] = fixture.market;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const predValue = t2;
  let t3;
  if ($[3] !== fixture.market || $[4] !== labels) {
    t3 = formatMarketName(fixture.market, labels);
    $[3] = fixture.market;
    $[4] = labels;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const marketName = t3;
  const t4 = `flex items-center justify-between gap-2 ${compact ? "text-[11px]" : "text-xs"}`;
  let t5;
  if ($[6] !== marketName) {
    t5 = <span className="text-muted-foreground truncate">{marketName}</span>;
    $[6] = marketName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== predValue) {
    t6 = <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">{predValue}</span>;
    $[8] = predValue;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export default function PredictionHistory({
  labels
}: PredictionHistoryProps) {
  const sdk = useFansUnitedSDK();
  const [predictions, setPredictions] = useState<PredictionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const fetchPredictions = useCallback(async (startAfter?: string) => {
    if (!sdk) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      limit: 12
    };
    if (startAfter) params.startAfter = startAfter;
    try {
      const response = (await sdk.predictor.getMyPredictions(params)) as unknown as PredictionsResponse;
      if (startAfter) {
        setPredictions(prev => [...prev, ...response.data]);
      } else {
        setPredictions(response.data);
      }
      setCursor(response.meta.pagination.nextPageStartsAfter || null);
    } catch {
      if (!startAfter) setPredictions([]);
      setCursor(null);
    }
  }, [sdk]);
  useEffect(() => {
    setLoading(true);
    fetchPredictions().finally(() => setLoading(false));
  }, [fetchPredictions]);
  const handleLoadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    await fetchPredictions(cursor);
    setLoadingMore(false);
  };
  const getStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
      WON: labels.won || "Won",
      LOST: labels.lost || "Lost",
      ACTIVE: labels.active || "Active",
      PARTIALLY_WON: labels.partiallyWon || "Partial",
      CANCELED: labels.canceled || "Canceled"
    };
    return map[status] || status;
  };
  return <div>
            <h2 className="mb-4 text-lg font-semibold text-foreground">{labels.predictionHistory || "History"}</h2>

            {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="animate-pulse rounded-lg bg-card p-3">
                            <div className="mb-2 flex items-center justify-between">
                                <div className="h-4 w-16 rounded-full bg-muted" />
                                <div className="h-3 w-10 rounded bg-muted" />
                            </div>
                            <div className="mb-2 h-4 w-3/4 rounded bg-muted" />
                            <div className="h-3 w-1/2 rounded bg-muted" />
                        </div>)}
                </div> : predictions.length === 0 ? <div className="rounded-lg bg-card p-8 text-center">
                    <Trophy className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">{labels.noPredictions || "No predictions yet"}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {labels.noPredictionsDescription || "Start predicting to see your history here"}
                    </p>
                </div> : <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {predictions.map(prediction => {
          const config = statusConfig[prediction.status] || statusConfig.CANCELED;
          const StatusIcon = config.icon;
          const singleMatch = isSingleMatchPrediction(prediction);
          const sharedMatch = singleMatch ? prediction.fixtures[0]?.matchModel : null;
          return <div key={prediction.id} className="flex flex-col gap-2 rounded-lg bg-card p-3">
                                    {/* Header: status + points + date */}
                                    <div className="flex items-center justify-between">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${config.bg} ${config.text}`}>
                                            <StatusIcon className="h-2.5 w-2.5" />
                                            {getStatusLabel(prediction.status)}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-primary">
                                                {prediction.points > 0 ? "+" : ""}
                                                {prediction.points} pts
                                            </span>
                                            <span className="text-[10px] text-muted-foreground">
                                                {dateFormatter.format(new Date(prediction.createdAt))}
                                            </span>
                                        </div>
                                    </div>

                                    {singleMatch && sharedMatch ? <>
                                            {/* Single match: show match once, list predictions below */}
                                            <MatchRow match={sharedMatch} />
                                            <div className="flex flex-col gap-1.5 border-t border-border/30 mt-1 pt-2">
                                                {prediction.fixtures.map((fixture, idx) => <FixturePredictionRow key={`${fixture.matchId}-${idx}`} fixture={fixture} labels={labels} compact />)}
                                            </div>
                                        </> : (/* Multiple matches: show each fixture with its match */
            <div className="flex flex-col gap-2">
                                            {prediction.fixtures.map((fixture_0, idx_0) => <div key={`${fixture_0.matchId}-${idx_0}`} className={`flex flex-col gap-1 ${idx_0 > 0 ? "border-t border-border/30 mt-1 pt-2" : ""}`}>
                                                    {fixture_0.matchModel ? <MatchRow match={fixture_0.matchModel} /> : <p className="text-xs text-muted-foreground">{fixture_0.matchId}</p>}
                                                    <FixturePredictionRow fixture={fixture_0} labels={labels} compact />
                                                </div>)}
                                        </div>)}
                                </div>;
        })}
                    </div>

                    {cursor && <button onClick={handleLoadMore} disabled={loadingMore} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 cursor-pointer">
                            {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
                            {labels.loadMore || "Load More"}
                        </button>}
                </>}
        </div>;
}